import {
	MSDYNAMICS_FALLBACK_PIPELINE_ID,
	MSDYNAMICS_FALLBACK_STAGE_PREFIX,
	MsdynamicsPipeline,
	MsdynamicsPipelineKind,
} from "../schema/dynamics";

/**
 * Which id space a pipeline id belongs to.
 *
 * `deal-config` echoes this as `pipelineKind`, so the config tab never has to derive it.
 * The manual create-opportunity modal has no such echo (its pipeline is picked per request),
 * which is why the rule lives here as a pure function both can use.
 */
export const pipelineKindOf = (
	pipelineId: string | null | undefined
): MsdynamicsPipelineKind | null => {
	if (!pipelineId) {
		return null;
	}

	return pipelineId === MSDYNAMICS_FALLBACK_PIPELINE_ID
		? MsdynamicsPipelineKind.SALES_STAGE
		: MsdynamicsPipelineKind.BUSINESS_PROCESS_FLOW;
};

/** Which id space a stage id belongs to — `salesstagecode:<optionValue>` vs a `processstageid` GUID. */
export const stageKindOf = (stageId: string | null | undefined): MsdynamicsPipelineKind | null => {
	if (!stageId) {
		return null;
	}

	return stageId.startsWith(MSDYNAMICS_FALLBACK_STAGE_PREFIX)
		? MsdynamicsPipelineKind.SALES_STAGE
		: MsdynamicsPipelineKind.BUSINESS_PROCESS_FLOW;
};

/**
 * Whether the stage may be saved together with the pipeline.
 *
 * A Business Process Flow stage and a `salesstagecode` option value are **not**
 * interchangeable: the sync writes the former to `processid`/`stageid` and the latter to
 * `salesstagecode`, and it silently **drops** a mismatched stage rather than writing it into
 * the wrong attribute. A selection that would be ignored must therefore never be saved.
 */
export const isStageCompatibleWithPipeline = (
	pipelineId: string | null | undefined,
	stageId: string | null | undefined
): boolean => {
	if (!stageId) {
		return true;
	}

	const pipelineKind = pipelineKindOf(pipelineId);

	return pipelineKind !== null && pipelineKind === stageKindOf(stageId);
};

/**
 * The stage id that may stay selected for `pipelineId` — `null` whenever the stored stage
 * belongs to the other id space or to a pipeline the environment no longer returns.
 */
export const resolveSelectableStageId = (
	pipelines: MsdynamicsPipeline[],
	pipelineId: string | null | undefined,
	stageId: string | null | undefined
): string | null => {
	if (!stageId || !isStageCompatibleWithPipeline(pipelineId, stageId)) {
		return null;
	}

	const pipeline = pipelines.find((candidate) => candidate.id === pipelineId);

	return pipeline?.stages.some((stage) => stage.id === stageId) ? stageId : null;
};
